import * as React from 'react';
import { FocusEvent, MouseEvent, useState } from 'react';
import { Clear, Search } from '@youlify/icons';
import classNames from 'classnames';
import Input from 'rc-input';

import { useStyle } from '../style/input-trigger';
import { FilterInputTriggerProps } from '../types';

const FilterInputTrigger: React.FC<FilterInputTriggerProps> = ({
  value,
  inputValue,
  disabled,
  selected,
  prefix,
  suffix,
  allowClear = true,
  baseSelectedBgColor,
  className,
  style,
  onClear,
  ...restProps
}) => {
  const [wrapSSR, hashId] = useStyle('youlify-filter');
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isSelected =
    selected !== undefined ? !!selected : value !== undefined && value !== '' && value !== null;
  const isDisabled = disabled === true;
  const rootClass = classNames('youlify-filter-input-trigger', hashId, className, {
    'youlify-filter-input-trigger-hover': isHovered && !isFocused && !isDisabled && !isSelected,
    'youlify-filter-input-trigger-pressed': isFocused,
    'youlify-filter-input-trigger-selected': isSelected,
    'youlify-filter-input-trigger-disabled': isDisabled,
  });

  const rootStyle = {
    ...style,
    ...(baseSelectedBgColor ? { '--base-selected-bg-color': baseSelectedBgColor } : {}),
  };

  const handleMouseEnterOrLeave = (isEnter?: boolean) => (e: MouseEvent<HTMLInputElement>) => {
    setIsHovered(!!isEnter);
    isEnter ? restProps?.onMouseEnter?.(e) : restProps?.onMouseLeave?.(e);
  };

  const handleFocusOrBlur = (isFocus?: boolean) => (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(!!isFocus);
    isFocus ? restProps?.onFocus?.(e) : restProps?.onBlur?.(e);
  };

  const handleClear = (e: MouseEvent<HTMLElement>) => {
    setIsFocused(false);
    onClear?.(e);
  };

  prefix = prefix || <Search />;
  suffix =
    suffix || (allowClear && isSelected && !isDisabled ? <Clear onClick={handleClear} /> : null);

  return wrapSSR(
    <div
      className={rootClass}
      style={rootStyle}
      onMouseEnter={handleMouseEnterOrLeave(true)}
      onMouseLeave={handleMouseEnterOrLeave(false)}
      onFocus={handleFocusOrBlur(true)}
      onBlur={handleFocusOrBlur(false)}
    >
      <Input {...restProps} value={inputValue} prefix={prefix} suffix={suffix} />
    </div>,
  );
};

export default FilterInputTrigger;
